import Sugar from 'sugar';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { callLambda, getActionFromLambdaResults } from '../../../../../helpers/lambda.js';

import NSJobComponent from '../../../job.js';

import JobSchema from '../../../../../models/job-schema.js';
import Company from '../../../../../models/company.js';
import CommercialRelationship, { NetworkAccessRequest } from '../../../../../models/commercial-relationship.js';

import { ShareableRelationship, loadShareableConnections } from '../../../../../helpers/shareable-connections.js';

import '../../../../../ns-elements/ns-connection-picker.js';
import '../../../../../ns-elements/ns-job-layout.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/company/network-access/approve.json';
import { jobSchema } from '../../../job-schema.js';

@customElement(NSCompanyNetworkAccessApprove.is)
export default class NSCompanyNetworkAccessApprove extends NSJobComponent {
  static get is() {
    return 'ns-company-network-access-approve';
  }

  @state()
  private __requesterCompany?: Company;

  @state()
  private __grantorCompany?: Company;

  @state()
  private __request?: NetworkAccessRequest;

  /** The specific relationship this request lives on — companies can have multiple concurrent
   * relationships, so this is resolved once in connectedCallback and reused for every lambda
   * call below rather than re-derived (and potentially re-picked) each time. */
  @state()
  private __relationship?: CommercialRelationship;

  @state()
  private __connections: ShareableRelationship[] = [];

  @state()
  private __selected: ReadonlySet<string> = new Set();

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { approve: { type: 'hidden' }, reject: { type: 'hidden' } } });
  }

  async connectedCallback() {
    try {
      await super.connectedCallback();

      if (!this._db) throw new Error('Unable to find db service');

      if (!this._auth) throw new Error('Unable to find auth service');

      const requesterId = this.job.inputValues.REQUESTER_COMPANY_ID;
      const grantorId = this.job.inputValues.GRANTOR_COMPANY_ID;
      if (!requesterId || typeof requesterId !== 'string') throw new Error('Missing required requester company ID');
      if (!grantorId || typeof grantorId !== 'string') throw new Error('Missing required grantor company ID');

      this.__requesterCompany = await this._db.getById<Company>('company', requesterId);
      this.__grantorCompany = await this._db.getById<Company>('company', grantorId);

      if (!this.__requesterCompany) throw new Error(`Unable to find company with id ${requesterId}`);
      if (!this.__grantorCompany) throw new Error(`Unable to find company with id ${grantorId}`);

      const relationship = await this.__findRelationship(requesterId, grantorId);
      if (!relationship) throw new Error(`Could not find a connection between ${requesterId} and ${grantorId}`);
      this.__relationship = relationship;

      this.__request = (relationship.networkAccessRequests || []).find((r) => {
        return (
          r.requesterCompanyId === requesterId && r.grantorCompanyId === grantorId && r.status === 'AWAITING_RESPONSE'
        );
      });
      if (!this.__request) throw new Error('Could not find a pending network access request');

      this.__connections = await loadShareableConnections(this._db, grantorId, requesterId, 'DOWN');

      // A re-ask (11-depth-increase-consent-gating.md): the grantor already tagged/shared these
      // exact rows on this relationship before — pre-select them so the natural action is
      // "confirm/extend", not "pick from scratch again". `__isReExtension`'s render-time copy
      // change relies on the same signal (`relationship.linkedRelationshipIds`).
      if (this.__isReExtension) {
        const alreadyLinked = new Set(this.__relationship?.linkedRelationshipIds ?? []);
        this.__selected = new Set(
          this.__connections.filter((r) => alreadyLinked.has(r.relationshipId)).map((r) => r.relationshipId),
        );
      }
    } catch (err: unknown) {
      this.failJob(err, 'Unexpected error occurred while loading the request');
    }
  }

  private async __findRelationship(requesterId: string, grantorId: string) {
    if (!this._db) throw new Error('Unable to find db service');

    const relationshipId = this.job.inputValues.RELATIONSHIP_ID;
    if (relationshipId && typeof relationshipId === 'string') {
      return this._db.getById<CommercialRelationship>('comRelationship', relationshipId);
    }

    // Jobs spawned before RELATIONSHIP_ID was passed: the most recent relationship with a pending request wins.
    const relationshipQuery = await this._db.query('comRelationship', {
      status: { $eq: 'ACTIVE' },
      'parties.companyId': { $in: [requesterId, grantorId] },
    });
    const relationships = relationshipQuery.results as unknown as CommercialRelationship[];
    return relationships
      .filter((r) => {
        const partyIds = r.parties.map((p) => p.companyId);
        return partyIds.includes(requesterId) && partyIds.includes(grantorId);
      })
      .sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime())
      .find((r) =>
        (r.networkAccessRequests || []).some(
          (req) =>
            req.requesterCompanyId === requesterId &&
            req.grantorCompanyId === grantorId &&
            req.status === 'AWAITING_RESPONSE',
        ),
      );
  }

  // True when this grantor has already linked something on this exact relationship before — a
  // depth-increase re-ask (11-depth-increase-consent-gating.md) or a repeat cascade bump, as
  // opposed to a genuine first-time ask. `linkedRelationshipIds` is only ever populated by this
  // same job's own approve action, so its presence is a reliable "have I done this before" signal.
  private get __isReExtension(): boolean {
    return (this.__relationship?.linkedRelationshipIds?.length ?? 0) > 0;
  }

  private async __approve() {
    if (!this._db) throw new Error('Missing db service');
    if (!this._auth) throw new Error('Missing auth service');
    if (!this.__requesterCompany?.id) throw new Error('Missing required requester company');
    if (!this.__grantorCompany?.id) throw new Error('Missing required grantor company');

    if (this.__selected.size < 1) {
      this.fatalError('Select at least one company to share, or reject the request instead.');
      return;
    }

    try {
      this.loading('Sharing Connections');
      const lambdaResult = await callLambda(
        'company/network-access-request-respond',
        'POST',
        this._auth.token,
        JSON.stringify({
          requester: this.__requesterCompany.id,
          grantor: this.__grantorCompany.id,
          relationshipId: this.__relationship?.id,
          decision: 'approve',
          sharedRelationshipIds: [...this.__selected],
        }),
      );

      if (lambdaResult.res.code !== 200) throw new Error('Failed to respond to the network access request');
      this.loaded();
      this.outcome('approve');
    } catch (err) {
      this.loaded();
      this.failJob(err, 'Unexpected error occurred while approving the request');
    }
  }

  private async __reject() {
    if (!this._db) throw new Error('Missing db service');
    if (!this._auth) throw new Error('Missing auth service');
    if (!this.__requesterCompany?.id) throw new Error('Missing required requester company');
    if (!this.__grantorCompany?.id) throw new Error('Missing required grantor company');

    try {
      this.loading('Rejecting Request');
      const lambdaResult = await callLambda(
        'company/network-access-request-respond',
        'POST',
        this._auth.token,
        JSON.stringify({
          requester: this.__requesterCompany.id,
          grantor: this.__grantorCompany.id,
          relationshipId: this.__relationship?.id,
          decision: 'reject',
        }),
      );

      await getActionFromLambdaResults(lambdaResult, 'network_access_request_rejected');
      this.loaded();
      this.outcome('reject');
    } catch (err) {
      this.loaded();
      this.failJob(err, 'Unexpected error occurred while rejecting the request');
    }
  }

  render() {
    const requesterCompany = this.__requesterCompany?.name
      ? Sugar.String.capitalize(this.__requesterCompany.name, true, true)
      : '';
    const requestedDepth = this.__request?.requestedDepth;
    const isReExtension = this.__isReExtension;

    const headline = isReExtension
      ? `${requesterCompany} has increased how deep they'd like to see into your supply chain${
          requestedDepth !== undefined ? ` (now ${requestedDepth} tier${requestedDepth === 1 ? '' : 's'})` : ''
        }.`
      : `${requesterCompany} would like to see your connections.`;
    const pickerLabel = isReExtension ? 'Extend your shared connections' : 'Search your connections to share';
    const helperText = isReExtension
      ? html`
          Connections you already share are pre-selected below. Confirm to extend them to the new depth or add more.
          Unticking one doesn't stop sharing it, and rejecting keeps what you share at the old depth.
        `
      : html`
          ${requesterCompany} will see the connections you pick, and so will the companies ${requesterCompany} shares
          with further up its chain, within the depth they asked for. Companies you connect with later aren't shared
          automatically; ${requesterCompany} would need to ask again.
        `;

    return html`
      <ns-job-layout>
        <p slot="intro">${headline}</p>
        ${!isReExtension || this.__request?.notes
          ? html`
              <div class="job-panel">
                <h3>Their reason</h3>
                <p>${this.__request?.notes || 'No reason given.'}</p>
              </div>
            `
          : null}
        ${this.__connections.length > 0
          ? html`
              <ns-connection-picker
                class="job-panel"
                label=${pickerLabel}
                select-all-label="Approve All"
                share-with=${this.__requesterCompany?.name ?? ''}
                .connections=${this.__connections}
                .selected=${this.__selected}
                @connections-change=${(e: CustomEvent<{ selected: Set<string> }>) =>
                  (this.__selected = e.detail.selected)}
              >
                <p class="small">${helperText}</p>
              </ns-connection-picker>
            `
          : html`
              <div class="job-panel">
                <p>You have no connections to share yet.</p>
              </div>
            `}
      </ns-job-layout>
    `;
  }

  renderActions() {
    return html`
      <wa-button variant="brand" appearance="outlined" type="button" @click=${() => this.__reject()}>Reject</wa-button>
      ${this.__connections.length > 0
        ? html`
            <wa-button variant="brand" type="button" @click=${() => this.__approve()}>Approve Selected</wa-button>
          `
        : nothing}
    `;
  }
}
